import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { PostLoginNavigate } from '@/features/auth/components/post-login-navigate'
import { postLoginDestination } from '@/storage/post-login-redirect'
import { useAccountStore } from '@/stores/account-store'
import { authService } from '@/services/auth/auth-service'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { OtpInput } from '@/components/otp-input'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { authErrorName } from '@/services/analytics/auth-error-name'

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const session = useAccountStore((s) => s.session)
  const passedEmail = (location.state as { email?: string })?.email ?? ''

  const [email, setEmail] = useState(passedEmail)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [resendStatus, setResendStatus] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)
  const { cooling, start: startCooldown } = useResendCooldown(5)

  const verify = async (value: string) => {
    if (value.length < 6 || isPending) return
    setError(null)
    setIsPending(true)
    try {
      await useAccountStore.getState().confirmRegistration(email, value)
      const autoSignedIn = Boolean(useAccountStore.getState().session)
      trackEvent(EVENTS.EMAIL_VERIFIED, { auto_signed_in: autoSignedIn })
      if (autoSignedIn) {
        navigate(postLoginDestination(), { replace: true })
      } else {
        useAccountStore.getState().setLoginMessage('Email verified! Please sign in.')
        navigate('/login', { replace: true })
      }
    } catch (err) {
      trackEvent(EVENTS.EMAIL_VERIFICATION_FAILED, { error_name: authErrorName(err) })
      setCode('')
      setError((err as Error).message ?? 'Verification failed')
      setIsPending(false)
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void verify(code)
  }

  const handleResend = async () => {
    startCooldown()
    setResendStatus(null)
    try {
      await authService.resendVerificationCode(email)
      trackEvent(EVENTS.VERIFICATION_CODE_RESENT, {})
      setResendStatus('Code sent! Check your email.')
    } catch (err) {
      setResendStatus((err as Error).message ?? 'Failed to resend code')
    }
  }

  if (session) {
    return <PostLoginNavigate />
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Verify Your Email</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the verification code sent to your email.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        {resendStatus && (
          <div className="rounded-md bg-positive/10 p-3 text-sm text-positive">{resendStatus}</div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-foreground">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <span className="text-sm font-medium text-foreground">Verification code</span>
            <OtpInput value={code} onChange={setCode} onComplete={verify} disabled={isPending} />
          </div>

          <Button type="submit" className="w-full" disabled={isPending || code.length < 6}>
            {isPending ? 'Verifying...' : 'Verify email'}
          </Button>
        </form>

        <div className="space-y-2 text-center text-sm text-muted-foreground">
          <p>
            Didn&apos;t receive a code?{' '}
            <button
              type="button"
              onClick={handleResend}
              className="text-primary hover:underline disabled:opacity-50"
              disabled={!email || cooling}
            >
              Resend code
            </button>
          </p>
          <p>
            <Link to="/login" className="text-primary hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
